import { useEffect, useState } from "react";
import { AUTH_SCREENS } from "../../constant/constant";
import LoginPage from "./LoginPage";
import ForgotPasswordPage from "./ForgotPasswordPage";
import EmailSentPage from "./EmailSentPage";
import ResetPassword from "../ForgotPassword/ForgotPassword";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useRecaptchaV3 from "../../hooks/recaptchaV3";
import { useTranslation } from "react-i18next";
import { getPath } from "../../helper/commonHelper";
import { useLoginHandlers } from "../Login/useLoginHandlers";
import { recaptchaSiteKey } from "../../utils/ApiHandler";
import { get, isEmpty } from "lodash";
import validator from "validator";
import { permissionsFn, redirectToPath } from "../../helper/com-helper";

/**
 * AuthRouter
 *
 * Top-level orchestrator for the auth flow.
 * Manages screen transitions and passes handlers down to individual pages.
 *
 * Screen flow:
 *   LOGIN ──[Forgot password?]──► FORGOT_PASSWORD ──[Submit]──► EMAIL_SENT
 *   EMAIL_SENT ──[Back to login]──► LOGIN
 *   FORGOT_PASSWORD ──[Back to login]──► LOGIN
 */
const initialLoginState = {
  email: "",
  password: "",
  showPassword: false,
};

const AuthRouter = () => {
  const [screen, setScreen] = useState(AUTH_SCREENS.LOGIN);
  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [path] = useState(globalThis.location.href);
  const user = JSON.parse(localStorage.getItem("user") || null);
  const { t } = useTranslation();
  const executeRecaptcha = useRecaptchaV3(recaptchaSiteKey);

  const [formData, setFormData] = useState(initialLoginState);

  const {
    submitUser,
    forgotPasswordSubmit,
    handleSSO,
    emailError,
    setEmailError,
    onOKTAButtonClick,
    onEffectFn,
  } = useLoginHandlers({
    dispatch,
    navigate,
    t,
    executeRecaptcha,
  });
  const handleClickShowPassword = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword,
    });
  };

  const handleChange = (prop) => (event) => {
    const value =
      prop === "email" ? event.target.value?.toLowerCase() : event.target.value;

    if (prop === "email") {
      if (value && !validator.isEmail(value)) {
        setEmailError("emailAddressValidation");
      } else {
        setEmailError("");
      }
    }

    setFormData({
      ...formData,
      [prop]: value,
    });
  };

  const { email, password, showPassword } = formData;

  const isDisabled = !email || !password || Boolean(emailError);

  useEffect(() => {
    if (!isEmpty(user)) {
      const permissions = permissionsFn(user);
      const roles = get(user, "roles", []);
      const path = getPath(permissions, roles);
      if (path) {
        redirectToPath(path);
      }
    }
  }, [user]);

  useEffect(() => {
    onEffectFn(path, setScreen);
  }, [onEffectFn, path]);

  const resetForms = () => {
    setFormData(initialLoginState);
    setEmailError("");
    //  setIsSubmitting(false);
  };


  const goTo = (targetScreen) => {
    resetForms();
    setScreen(targetScreen);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  switch (screen) {
    case AUTH_SCREENS.FORGOT_PASSWORD:
      return (
        <ForgotPasswordPage
          forgotForm={formData}
          errors={emailError}
          isSubmitting={false}
          onFieldChange={() => handleChange("email")}
          onSubmit={() => forgotPasswordSubmit(formData.email)}
          onBackToLogin={() => goTo(AUTH_SCREENS.LOGIN)}
          onDidntReceive={() => { }}
        />
      );

    case AUTH_SCREENS.EMAIL_SENT:
      return (
        <EmailSentPage
          onBackToLogin={() => goTo(AUTH_SCREENS.LOGIN)}
        />
      );

    case AUTH_SCREENS.RESET_PASSWORD:
      return <ResetPassword />;

    case AUTH_SCREENS.LOGIN:
    default:
      return (
        <LoginPage
          loginForm={formData}
          errors={emailError}
          isLoginDisabled={isDisabled}
          isSubmitting={false}
          onFieldChange={handleChange}
          onTogglePassword={handleClickShowPassword}
          onSubmit={submitUser}
          onOktaLogin={onOKTAButtonClick}
          onMicrosoftLogin={handleSSO}
          onForgotPassword={() => goTo(AUTH_SCREENS.FORGOT_PASSWORD)}
        />
      );
  }
};

export default AuthRouter;
